import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ContactCard } from "./ContactCard";
import type { MCPServer } from "@/types";
import { ShieldCheck, Globe2, Plus, Search, MoreVertical, Pencil, Trash, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ContactListProps {
  contacts: MCPServer[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onAdd?: () => void;
  onEdit?: (contact: MCPServer) => void;
  onDelete?: (contact: MCPServer) => void;
  onSearch?: () => void;
  onRefresh?: (contact: MCPServer) => void;
}

export const ContactList = ({
  contacts,
  selectedId,
  onSelect,
  onAdd,
  onEdit,
  onDelete,
  onSearch,
  onRefresh,
}: ContactListProps) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const menuPortalRef = useRef<HTMLDivElement | null>(null);

  const toggleMenu = (id: string) => {
    if (openMenuId === id) {
      setOpenMenuId(null);
      setMenuPosition(null);
    } else {
      const btn = menuButtonRefs.current[id];
      if (btn) {
        const rect = btn.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + window.scrollY,
          left: rect.right + window.scrollX - 160, // 160px is approx menu width, adjust as needed
        });
      }
      setOpenMenuId(id);
    }
  };

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  // Only close menu if click is outside the portal menu
  useEffect(() => {
    if (!openMenuId) return;
    const handle = (e: MouseEvent) => {
      if (
        menuPortalRef.current &&
        (e.target instanceof Node) &&
        menuPortalRef.current.contains(e.target)
      ) {
        // Click inside menu, do nothing
        return;
      }
      closeMenu();
    };
    const handleScroll = () => closeMenu();
    window.addEventListener("mousedown", handle);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("mousedown", handle);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openMenuId]);

  return (
    <section className="glass-card border border-white/40 p-2 sm:p-4 shadow-xl dark:border-white/10">
      <div className="flex items-start justify-between gap-2">
        {/* Header */}
        <div className="space-y-0.5 sm:space-y-1 min-w-0 flex-1">
          <p className="text-[10px] sm:text-xs uppercase tracking-wide text-primary">MCP Servers</p>
          <h2 className="hidden md:block text-base sm:text-lg font-semibold text-foreground">Your agent roster</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Tap an agent to open a real-time chat. <span className="hidden sm:inline">Each one maps to an MCP server endpoint.</span>
          </p>
        </div>
        {/* Actions + Info */}
        <div className="flex flex-col items-end gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-muted-foreground flex-shrink-0">
          <div className="flex items-center gap-1 sm:gap-2">
            <Button variant="outline" size="sm" onClick={onAdd} className="gap-1 sm:gap-2 border-cyan-500/20 h-7 sm:h-8 px-2 sm:px-3">
              <Plus className="h-3 w-3 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">Add</span>
            </Button>
            <Button variant="outline" size="sm" className="gap-1 sm:gap-2 border-cyan-500/20 h-7 sm:h-8 px-2 sm:px-3" onClick={onSearch}>
              <Search className="h-3 w-3 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">Search</span>
            </Button>
          </div>
            {/* Info panel: hidden on screens <900px */}
            <div className="hidden md:flex flex-col items-end gap-1">
            <span className="pill bg-white/70 text-[10px]">Enterprise-ready</span>
            <div className="flex items-center gap-1">
              <ShieldCheck className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-500" />
              <span>Secure by design</span>
            </div>
            <div className="flex items-center gap-1">
              <Globe2 className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
              <span>Streamable HTTP transport only</span>
            </div>
            </div>
        </div>
      </div>

      {/* Contacts */}
      <div className="mt-2 sm:mt-4 space-y-2 sm:space-y-3 max-h-[60vh] overflow-y-auto">
        {contacts.map(contact => (
          <div key={contact.id} className="relative">
            {/* <ContactCard contact={contact} isActive={selectedId === contact.id} onSelect={onSelect} /> */}
            <ContactCard contact={contact} onSelect={onSelect} />
            <div className="absolute right-2 top-2 sm:right-3 sm:top-3">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={e => {
                  e.stopPropagation();
                  toggleMenu(contact.id);
                }}
                aria-label="Contact menu"
                className="h-7 w-7 sm:h-8 sm:w-8"
                ref={el => {
                  menuButtonRefs.current[contact.id] = el;
                }}
              >
                <MoreVertical className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
              {/* Portal menu */}
              {openMenuId === contact.id && menuPosition
                ? createPortal(
                  // We need to keep the menu visible and prevent this is clipped by parent containers (different view port)
                    <div
                      ref={menuPortalRef}
                      style={{
                        position: "absolute",
                        top: menuPosition.top,
                        left: menuPosition.left,
                        zIndex: 9999,
                        minWidth: "8rem",
                        width: "9rem",
                      }}
                      className="rounded-xl border border-border/60 bg-white/95 p-1.5 sm:p-2 shadow-lg dark:bg-slate-900"
                    >
                      <button
                        type="button"
                        className="flex w-full items-center gap-1.5 sm:gap-2 rounded-md px-2 py-1.5 text-xs sm:text-sm text-foreground hover:bg-muted"
                        onClick={() => {
                          onEdit?.(contact);
                          closeMenu();
                        }}
                      >
                        <Pencil className="h-3 w-3 sm:h-4 sm:w-4" /> Edit
                      </button>
                      <button
                        type="button"
                        className="flex w-full items-center gap-1.5 sm:gap-2 rounded-md px-2 py-1.5 text-xs sm:text-sm text-foreground hover:bg-muted"
                        onClick={() => {
                          onRefresh?.(contact);
                          closeMenu();
                        }}
                      >
                        <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" /> Refresh
                      </button>
                      <button
                        type="button"
                        className="flex w-full items-center gap-1.5 sm:gap-2 rounded-md px-2 py-1.5 text-xs sm:text-sm text-rose-600 hover:bg-rose-50"
                        onClick={() => {
                          onDelete?.(contact);
                          closeMenu();
                        }}
                      >
                        <Trash className="h-3 w-3 sm:h-4 sm:w-4" /> Delete
                      </button>
                    </div>,
                    document.body
                  )
                : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
